/* ═══════════════════════════════════════════════════════════════
   Constants — Shared data used across all components
   ═══════════════════════════════════════════════════════════════ */

import type { TokenDef, NumberFormatLevel } from '../shared/types';

export const FONTS: string[] = [
  'Arial',
  'Arial Narrow',
  'Calibri',
  'Cambria',
  'Century Gothic',
  'Courier New',
  'Garamond',
  'Georgia',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
];

export const TOKENS: TokenDef[] = [
  { id: 'SpecNumber', label: 'Spec #', value: '{SpecNumber}', color: '#2d6b27' },
  { id: 'SpecName', label: 'Spec Name', value: '{SpecName}', color: '#1a6b8a' },
  { id: 'SpecNumName', label: 'Spec # - Name', value: '{SpecNumName}', color: '#2d5b8a' },
  { id: 'Page', label: 'Page #', value: '{Page}', color: '#8a6a1a' },
  { id: 'NumPages', label: 'Total Pages', value: '{NumPages}', color: '#7a2a9a' },
  { id: 'PageOfPages', label: 'Page # of #', value: '{PageOfPages}', color: '#9a1a3a' },
  { id: 'Date', label: 'Date', value: '{Date}', color: '#2a7a4a' },
  { id: 'Space', label: 'Space', value: '{Space}', color: '#888' },
  { id: 'Return', label: '\u21b5 Return', value: '\n', color: '#888' },
  { id: 'Line', label: 'Line', value: '{Line}', color: '#555' },
  { id: 'Image', label: 'Image', value: '{Image}', color: '#2a6a8a' },
];

export const POST_TOKENS: TokenDef[] = [];

/* Project-linked tokens — fill in from the actual project at export time.
   The word engine resolves them from the projectMeta map provided by the
   project-spec export pipeline (same data the smart-block engine uses).
   In the designer they preview with sample values. */
export const PROJECT_TOKENS: TokenDef[] = [
  { id: 'ProjectName', label: 'Project Name', value: '{Project.Name}', color: '#46627f' },
  {
    id: 'ClientProjectName',
    label: 'Client Project Name',
    value: '{Project.ClientProjectName}',
    color: '#46627f',
  },
  { id: 'ProjectNumber', label: 'Project #', value: '{Project.Number}', color: '#46627f' },
  { id: 'ProjectAddress', label: 'Project Address', value: '{Project.Address}', color: '#46627f' },
  { id: 'ClientName', label: 'Client', value: '{Client.Name}', color: '#46627f' },
  // Picked at export time (deliverable dropdown in the export dialog);
  // issuance comes from the spec's own issuance history.
  { id: 'Deliverable', label: 'Deliverable', value: '{Deliverable}', color: '#46627f' },
  { id: 'IssuanceName', label: 'Issuance', value: '{Issuance.Name}', color: '#46627f' },
  // The date the spec was ISSUED, written in the forma's date format. Distinct
  // from {Date} (print-time "today"): a template's date conveys the issuance,
  // which is why the importer maps template dates here and never to {Date}.
  { id: 'IssuanceDate', label: 'Issuance Date', value: '{IssuanceDate}', color: '#46627f' },
];

export const ALL_TOKENS: TokenDef[] = [...TOKENS, ...PROJECT_TOKENS, ...POST_TOKENS];

export const CSI_NUMBER_FORMATS: NumberFormatLevel[] = [
  {
    level: 1,
    options: [
      {
        id: 'part_dash',
        label: 'PART 1 -',
        lvlText: 'PART %1 -',
        numFmt: 'decimal',
        suff: 'space',
      },
      { id: 'part_space', label: 'PART 1', lvlText: 'PART %1', numFmt: 'decimal', suff: 'space' },
    ],
  },
  {
    level: 2,
    options: [
      { id: '1.01', label: '1.01', lvlText: '%1.%2', numFmt: 'decimalZero', suff: 'tab' },
      { id: '1.1', label: '1.1', lvlText: '%1.%2', numFmt: 'decimal', suff: 'tab' },
    ],
  },
  {
    level: 3,
    options: [{ id: 'A.', label: 'A.', lvlText: '%3.', numFmt: 'upperLetter', suff: 'tab' }],
  },
  {
    level: 4,
    options: [{ id: '1.', label: '1.', lvlText: '%4.', numFmt: 'decimal', suff: 'tab' }],
  },
  {
    level: 5,
    options: [{ id: 'a.', label: 'a.', lvlText: '%5.', numFmt: 'lowerLetter', suff: 'tab' }],
  },
  {
    level: 6,
    options: [
      { id: '1)', label: '1)', lvlText: '%6)', numFmt: 'decimal', suff: 'tab' },
      { id: '(1)', label: '(1)', lvlText: '(%6)', numFmt: 'decimal', suff: 'tab' },
    ],
  },
  {
    level: 7,
    options: [
      { id: 'a)', label: 'a)', lvlText: '%7)', numFmt: 'lowerLetter', suff: 'tab' },
      { id: '(a)', label: '(a)', lvlText: '(%7)', numFmt: 'lowerLetter', suff: 'tab' },
    ],
  },
  {
    level: 8,
    options: [
      { id: '(1)', label: '(1)', lvlText: '(%8)', numFmt: 'decimal', suff: 'tab' },
      { id: '1)', label: '1)', lvlText: '%8)', numFmt: 'decimal', suff: 'tab' },
    ],
  },
  {
    level: 9,
    options: [
      { id: '(a)', label: '(a)', lvlText: '(%9)', numFmt: 'lowerLetter', suff: 'tab' },
      { id: 'a)', label: 'a)', lvlText: '%9)', numFmt: 'lowerLetter', suff: 'tab' },
    ],
  },
];

export const SC = 0.82;
export const PPI = 96;

// ── Expose as globals for legacy JSX files ──
(window as any).FONTS = FONTS;
(window as any).TOKENS = TOKENS;
(window as any).POST_TOKENS = POST_TOKENS;
(window as any).ALL_TOKENS = ALL_TOKENS;
(window as any).CSI_NUMBER_FORMATS = CSI_NUMBER_FORMATS;
(window as any).SC = SC;
(window as any).PPI = PPI;
